import React, { useState, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { css } from '@emotion/core';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import { IS_IFRAME_MODE } from 'config/envVariable';

const facebookShare = url => () => {
    const fbShareUrl = 'https://www.facebook.com/sharer/sharer.php?u=' + url;
    if (isMobile) {
        if (IS_IFRAME_MODE) window.top.location = fbShareUrl;
        else window.location.href = fbShareUrl;
    } else window.open(fbShareUrl, '', 'toolbar=0,status=0,width=580,height=325');
};

const socialShare = (type, url) => () => {
    const shareUrl = {
        line: `https://line.me/R/msg/text/?${encodeURIComponent(url)}`,
        pcLine: `https://lineit.line.me/share/ui?url=${encodeURIComponent(url)}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(url)}`,
        whatsapp: `whatsapp://send?text=${encodeURIComponent(url)}`
    };

    const targetUrl = !isMobile && type === 'line' ? shareUrl.pcLine : shareUrl[type];
    window.open(targetUrl, '__blank');
};

export const modes = {
    COMPACT: 'compact',
    FULL: 'full'
};

function SocialShare({ mode = modes.COMPACT, url }) {
    const urlRef = useRef('');
    const [anchorEl, setAnchorEl] = useState(null);

    const open = Boolean(anchorEl);

    function handleClick(event) {
        setAnchorEl(event.currentTarget);
    }
    function handleClose() {
        setAnchorEl(null);
    }
    function copyUrl(event) {
        const input = urlRef.current;
        input.type = 'text';
        input.select();
        document.execCommand('copy');
        input.type = 'hidden';
        handleClick(event);
    }

    return (
        <div css={containerStyle}>
            <input value={url} ref={urlRef} type="hidden" />
            {mode === modes.COMPACT && (
                <Typography css={textStyle} variant="body2">
                    {i18n.t('label.share')}
                </Typography>
            )}
            <div>
                <IconButton css={btnStyle} size="small" onClick={facebookShare(url)}>
                    <img
                        css={iconStyle}
                        src="https://assets.fevercdn.com/promotion/icon/facebook.svg"
                        alt="facebookShare"
                    />
                </IconButton>
                {mode === modes.FULL && <Typography variant="subtitle2">Facebook</Typography>}
            </div>
            <div>
                <IconButton css={btnStyle} size="small" onClick={socialShare('line', url)}>
                    <img
                        css={iconStyle}
                        src="https://assets.fevercdn.com/promotion/icon/line.svg"
                        alt="lineShare"
                    />
                </IconButton>
                {mode === modes.FULL && <Typography variant="subtitle2">Line</Typography>}
            </div>
            <div>
                <IconButton css={btnStyle} size="small" onClick={socialShare('twitter', url)}>
                    <img
                        css={iconStyle}
                        src="https://assets.fevercdn.com/promotion/icon/twitter.svg"
                        alt="twitterShare"
                    />
                </IconButton>
                {mode === modes.FULL && <Typography variant="subtitle2">Twitter</Typography>}
            </div>
            <div>
                <IconButton css={btnStyle} size="small" onClick={socialShare('whatsapp', url)}>
                    <img
                        css={iconStyle}
                        src="https://assets.fevercdn.com/promotion/icon/whatsapp.svg"
                        alt="whatsappShare"
                    />
                </IconButton>
                {mode === modes.FULL && <Typography variant="subtitle2">Whatsapp</Typography>}
            </div>
            <div>
                <IconButton css={btnStyle} variant="contained" size="small" onClick={copyUrl}>
                    <img
                        css={iconStyle}
                        src="https://assets.fevercdn.com/promotion/icon/link.svg"
                        alt="copyShare"
                    />
                </IconButton>
                {mode === modes.FULL && <Typography variant="subtitle2">Link</Typography>}
            </div>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                css={css(`padding: 16px;`)}
            >
                <Typography css={css(`padding: 8px 16px;`)}>{i18n.t('label.share_url')}</Typography>
            </Popover>
        </div>
    );
}

SocialShare.propTypes = {
    url: PropTypes.string
};

export default SocialShare;

const textStyle = css`
    margin-left: 8px;
`;

const btnStyle = css`
    margin: 0 5px;
`;

const iconStyle = css`
    width: 36px;
    height: 36px;
`;

const containerStyle = css`
    padding: 2px;
    display: flex;
    align-items: center;
    border-radius: 22px;
    backdrop-filter: blur(8px);
    background-color: rgb(255 255 255 / 32%);
    & button:last-child {
        margin-right: 0;
    }
    & > div {
        padding: 0 4px;
    }
`;
